/**
 * Hooks do Sistema de Templates de Tenant.
 *
 * Concentra as consultas e ações da tela central de Templates do Sistema.
 *
 * @version 1.7.0
 * RELEASE: 2026-09-08 v1.7.0 - Novo `useTemplateModuleItemsBatch`: carrega os itens
 *          de todos os módulos em paralelo, para a contagem aparecer já na
 *          abertura do modal de geração (antes só aparecia ao clicar no módulo).
 * RELEASE: 2026-09-08 v1.6.0 - Fim da rotina automática de 1 minuto: toda ação que
 *          enfileira trabalho (gerar, conferir, aplicar, reprocessar) dispara o
 *          processamento na hora.
 * RELEASE: 2026-09-08 v1.5.0 - Itens por módulo (exportação), itens do pacote
 *          (importação) e seleção granular junto com o responsável.
 * RELEASE: 2026-09-08 v1.4.0 - Download do pacote gerado e acionamento manual do
 *          processamento da fila.


 * RELEASE: 2026-09-08 v1.3.0 - Fase 5: indicadores de acompanhamento.
 * RELEASE: 2026-09-08 v1.2.0 - Mapeamento de pessoas (responsável padrão).
 * RELEASE: 2026-09-08 v1.1.0 - Simulação, histórico, pendências, reprocessamento
 *          e aprovação do plano.
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (Fase 1 do plano).
 */

import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveTemplatePlan,
  createTemplateExportJob,
  downloadTemplateVersion,
  fetchTemplateVersionContent,
  previewTemplateModuleItems,
  runTemplateWorkerNow,
  getTemplateJob,
  getTemplateMetrics,
  getTemplatePlan,
  listTemplateJobs,
  listTemplatePackageVersions,
  listTemplatePackages,
  listTemplatePendingActions,
  listTemplateTenantMembers,
  setTemplatePlanOwner,
  requestTemplateRollback,
  retryTemplateJob,
  uploadTemplatePackage,
  type CreateExportInput,
  type TemplateCollectionItems,
  type TemplateSelection,
} from '@/lib/template-system/api';

/** Chaves de cache do módulo. */
export const systemTemplateKeys = {

  packages: (includeArchived: boolean) => ['system-templates', 'packages', includeArchived] as const,
  versions: (packageId: string) => ['system-templates', 'versions', packageId] as const,
  job: (jobId: string) => ['system-templates', 'job', jobId] as const,
  jobs: (limit: number) => ['system-templates', 'jobs', limit] as const,
  plan: (versionId: string) => ['system-templates', 'plan', versionId] as const,
  pending: (jobId: string) => ['system-templates', 'pending', jobId] as const,
  members: (tenantId: string) => ['system-templates', 'members', tenantId] as const,
  metrics: () => ['system-templates', 'metrics'] as const,
};

/**
 * Dispara o processamento da fila logo após enfileirar um trabalho.
 * Substitui a antiga rotina automática de 1 em 1 minuto. Uma falha aqui não
 * pode derrubar a operação do usuário: o trabalho segue na fila e pode ser
 * retomado pelo botão "Atualizar fila".
 */
function dispararProcessamento(): void {
  void runTemplateWorkerNow().catch(() => {
    /* silencioso de propósito — o botão manual é a saída de contingência */
  });
}



/** Lista os templates da conta. */
export function useTemplatePackages(includeArchived = false) {
  return useQuery({
    queryKey: systemTemplateKeys.packages(includeArchived),
    queryFn: () => listTemplatePackages(includeArchived),
    staleTime: 30_000,
  });
}

/** Lista as versões de um template. */
export function useTemplatePackageVersions(packageId: string | null) {
  return useQuery({
    queryKey: systemTemplateKeys.versions(packageId ?? ''),
    queryFn: () => listTemplatePackageVersions(packageId as string),
    enabled: !!packageId,
  });
}

/**
 * Acompanha uma execução. Enquanto o processo não termina, revalida a cada
 * 3 segundos para refletir o progresso na tela.
 */
export function useTemplateJob(jobId: string | null) {
  return useQuery({
    queryKey: systemTemplateKeys.job(jobId ?? ''),
    queryFn: () => getTemplateJob(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const finalizado = status === 'completed' || status === 'failed' || status === 'compensated';
      return finalizado ? false : 3000;
    },
  });
}

/** Cria a execução de exportação. */
export function useCreateTemplateExport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateExportInput) => createTemplateExportJob(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates', 'packages'] });
      dispararProcessamento();
    },

  });
}

/** Envia um arquivo de template e abre a importação. */
export function useUploadTemplatePackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, file }: { tenantId: string; file: File }) =>
      uploadTemplatePackage(tenantId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates', 'packages'] });
      dispararProcessamento();
    },

  });
}

/** Solicita a reversão de uma aplicação finalizada. */
export function useRequestTemplateRollback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applyJobId: string) => requestTemplateRollback(applyJobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates'] });
      dispararProcessamento();
    },

  });
}

/** Histórico recente de execuções da conta. */
export function useTemplateJobs(limit = 20) {
  return useQuery({
    queryKey: systemTemplateKeys.jobs(limit),
    queryFn: () => listTemplateJobs(limit),
    // Mantém a lista viva enquanto houver algo na fila ou em andamento.
    refetchInterval: (query) => {
      const emAndamento = (query.state.data ?? []).some(
        (j) => j.status === 'pending' || j.status === 'running' || j.status === 'compensating',
      );
      return emAndamento ? 4000 : false;
    },
  });
}

/** Simulação (plano) gerada para uma versão de template. */
export function useTemplatePlan(packageVersionId: string | null) {
  return useQuery({
    queryKey: systemTemplateKeys.plan(packageVersionId ?? ''),
    queryFn: () => getTemplatePlan(packageVersionId as string),
    enabled: !!packageVersionId,
  });
}

/** Pendências deixadas por uma execução. */
export function useTemplatePendingActions(jobId: string | null) {
  return useQuery({
    queryKey: systemTemplateKeys.pending(jobId ?? ''),
    queryFn: () => listTemplatePendingActions(jobId as string),
    enabled: !!jobId,
  });
}

/** Aprova a simulação e dispara a aplicação do template. */
export function useApproveTemplatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => approveTemplatePlan(planId, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates'] });
      dispararProcessamento();
    },

  });
}

/** Recoloca na fila uma execução que falhou. */
export function useRetryTemplateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => retryTemplateJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates'] });
      dispararProcessamento();
    },

  });
}

/** Pessoas ativas da conta, para o mapeamento de responsáveis. */
export function useTemplateTenantMembers(tenantId: string | null) {
  return useQuery({
    queryKey: systemTemplateKeys.members(tenantId ?? ''),
    queryFn: () => listTemplateTenantMembers(tenantId as string),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

/**
 * Salva o responsável padrão escolhido na revisão da simulação e, quando
 * informada, a seleção granular de itens que serão aplicados.
 */
export function useSetTemplatePlanOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      planId,
      ownerUserId,
      selection,
    }: {
      planId: string;
      ownerUserId: string;
      selection?: TemplateSelection | null;
    }) => setTemplatePlanOwner(planId, ownerUserId, selection ?? null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates'] });
    },
  });
}

/** Itens desta conta em um módulo, para a árvore de seleção da exportação. */
export function useTemplateModuleItems(moduleKey: string | null) {
  return useQuery({
    queryKey: ['system-templates', 'module-items', moduleKey ?? ''] as const,
    queryFn: () => previewTemplateModuleItems(moduleKey as string),
    enabled: !!moduleKey,
    staleTime: 60_000,
  });
}

/**
 * Itens desta conta em TODOS os módulos informados, buscados em paralelo.
 *
 * Usado pelo modal de geração para que a quantidade de cada módulo já apareça
 * na abertura, sem depender de o usuário clicar em cada um. Cada módulo tem sua
 * própria consulta (mesma chave de cache do `useTemplateModuleItems`), então
 * trocar de módulo continua instantâneo.
 */
export function useTemplateModuleItemsBatch(moduleKeys: string[], enabled: boolean) {
  const consultas = useQueries({
    queries: moduleKeys.map((key) => ({
      queryKey: ['system-templates', 'module-items', key] as const,
      queryFn: () => previewTemplateModuleItems(key),
      enabled,
      staleTime: 60_000,
    })),
  });

  const chaves = moduleKeys.join('|');

  return useMemo(() => {
    const itemsByModule: Record<string, TemplateCollectionItems[]> = {};
    const loadingModules: string[] = [];

    moduleKeys.forEach((key, indice) => {
      const consulta = consultas[indice];
      if (consulta?.data) {
        itemsByModule[key] = consulta.data;
      } else if (consulta?.isLoading) {
        loadingModules.push(key);
      }
    });

    return { itemsByModule, loadingModules };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaves, consultas.map((c) => `${c.status}`).join('|')]);
}

/** Itens que um pacote traz, por módulo, para a árvore de seleção da importação. */
export function useTemplatePackageItems(versionId: string | null) {
  return useQuery({
    queryKey: ['system-templates', 'package-items', versionId ?? ''] as const,
    queryFn: () => fetchTemplateVersionContent(versionId as string),
    enabled: !!versionId,
    staleTime: 60_000,
  });
}


/** Indicadores de acompanhamento do módulo (atualizados a cada 30s). */
export function useTemplateMetrics() {
  return useQuery({
    queryKey: systemTemplateKeys.metrics(),
    queryFn: () => getTemplateMetrics(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/** Baixa o arquivo `.uaizytemplate` de uma versão do template. */
export function useDownloadTemplateVersion() {
  return useMutation({
    mutationFn: (versionId: string) => downloadTemplateVersion(versionId),
  });
}

/**
 * Aciona o processamento da fila imediatamente, sem esperar a rotina
 * automática de 1 minuto. Atualiza todas as listas do módulo ao final.
 */
export function useRunTemplateWorker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => runTemplateWorkerNow(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates'] });
    },
  });
}
